import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon, PhoneIcon, MailIcon, ChevronDownIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from './Logo';
import { NavDropdown } from './navigation/NavDropdown';
import { NavIconGlyph } from './navigation/NavIconGlyph';
import { navItems } from '../data/navigation';
import type { Route } from '../hooks/useHashRoute';
type NavbarProps = {
  route: Route;
};
export function Navbar({
  route
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileGroup, setOpenMobileGroup] = useState<string | null>(null);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const closeMobile = () => {
    setIsMobileMenuOpen(false);
    setOpenMobileGroup(null);
  };
  return <header className={`sticky top-0 z-40 w-full bg-white transition-[padding,box-shadow] duration-200 ease-out ${isScrolled ? 'shadow-md py-2' : 'py-4'}`} data-id="element-593" data-mp="9nfyonhmzevj">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-594" data-mp="jmx20y9f1goc">
        <div className="flex justify-between items-center gap-6" data-id="element-595" data-mp="jkklmpjb3ede">
          <a href="#/" aria-label="AdROI home" className="flex flex-shrink-0 items-center rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-4" data-id="element-596" data-mp="okp0yc8ha321">
            <Logo size="sm" data-id="element-597" data-mp="3s04917xaq2kj" />
          </a>

          <nav aria-label="Main" className="hidden lg:flex items-center gap-7" data-id="element-598" data-mp="v4j5wo29buc">
            {navItems.map(item => item.mega ? <NavDropdown key={item.name} label={item.name} mega={item.mega} data-id="element-599" data-mp="5op9r3nbr3ji" /> : <a key={item.name} href={item.href} aria-current={item.route && item.route === route ? 'page' : undefined} className={`rounded-sm text-sm font-medium transition-colors duration-150 ease-out hover:text-brand-red focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red ${item.route && item.route === route ? 'text-brand-red' : 'text-gray-600'}`} data-id="element-600" data-mp="34b1u47u1qntq">
                  {item.name}
                </a>)}
          </nav>

          <div className="hidden lg:flex items-center gap-5" data-id="element-601" data-mp="2ie8mqox9unuv">
            <a href="tel:17022134801" className="hidden xl:flex items-center whitespace-nowrap text-sm font-medium text-gray-600 transition-colors duration-150 ease-out hover:text-brand-red" data-id="element-602" data-mp="2dem768tz0viw">
              <PhoneIcon className="h-4 w-4 mr-2" aria-hidden="true" data-id="element-603" data-mp="2ndv151ou6a59" />
              (702) 213-4801
            </a>
            <a href="#/contact" aria-current={route === 'contact' ? 'page' : undefined} className="whitespace-nowrap rounded-md bg-brand-red px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors duration-150 ease-out hover:bg-brand-red-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2" data-id="element-604" data-mp="2l9l8rgznjjw">
              CONTACT US
            </a>
          </div>

          <button onClick={() => setIsMobileMenuOpen(v => !v)} className="lg:hidden text-gray-600 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red rounded-sm" aria-label="Toggle menu" aria-expanded={isMobileMenuOpen} data-id="element-605" data-mp="57s6rb7g7icz">
            {isMobileMenuOpen ? <XIcon className="h-6 w-6" data-id="element-606" data-mp="o41t0lsi7f82" /> : <MenuIcon className="h-6 w-6" data-id="element-607" data-mp="o38uu97m1y1j" />}
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-608" data-mp="1bt364mca7ejo">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="lg:hidden bg-white border-t border-gray-100 overflow-hidden" data-id="element-609" data-mp="1gkumy55my5wm">
            <div className="max-h-[75vh] overflow-y-auto px-4 pt-2 pb-6 shadow-lg" data-id="element-610" data-mp="3f2zitm69u4j9">
              {navItems.map(item => item.children ? <div key={item.name} data-id="element-611" data-mp="3b4igdfxspi86">
                    <button type="button" onClick={() => setOpenMobileGroup(g => g === item.name ? null : item.name)} aria-expanded={openMobileGroup === item.name} className="flex w-full items-center justify-between rounded-md px-3 py-3 text-base font-medium text-gray-900 hover:bg-gray-50" data-id="element-612" data-mp="blzduu7o29zq">
                      {item.name}
                      <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform duration-200 ease-out ${openMobileGroup === item.name ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-613" data-mp="ufmtc7dnnvdn" />
                    </button>
                    {openMobileGroup === item.name && <ul className="mb-2 ml-3 border-l border-gray-200 pl-2" data-id="element-614" data-mp="2h9yrb9amgn62">
                        {item.children.map(child => <li key={child.name} data-id="element-615" data-mp="2mamgrqj7cvy6">
                            <a href={child.href} onClick={closeMobile} className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-brand-tint hover:text-brand-red" data-id="element-616" data-mp="2r8xqm19hz6ge">
                              <NavIconGlyph icon={child.icon} className="h-4 w-4 text-brand-red" data-id="element-617" data-mp="34yrfgjl05qq4" />
                              {child.name}
                            </a>
                          </li>)}
                      </ul>}
                  </div> : <a key={item.name} href={item.href} onClick={closeMobile} className="block rounded-md px-3 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 hover:text-brand-red" data-id="element-618" data-mp="1ewagl3ardn6i">
                    {item.name}
                  </a>)}
              <div className="pt-4 mt-4 border-t border-gray-100 flex flex-col space-y-4" data-id="element-619" data-mp="1xl7a34yc0bno">
                <a href="tel:17022134801" className="flex items-center px-3 text-base font-medium text-gray-600" data-id="element-620" data-mp="1tlezyntahxg7">
                  <PhoneIcon className="h-5 w-5 mr-3 text-gray-400" aria-hidden="true" data-id="element-621" data-mp="27j3jnsbmeyjo" />
                  (702) 213-4801
                </a>
                <a href="mailto:Results@GetAdROI.com" className="flex items-center px-3 text-base font-medium text-gray-600" data-id="element-622" data-mp="22jil3j0d4s7h">
                  <MailIcon className="h-5 w-5 mr-3 text-gray-400" aria-hidden="true" data-id="element-623" data-mp="87tyqvrsxnd2" />
                  Results@GetAdROI.com
                </a>
                <a href="#/contact" onClick={closeMobile} className="w-full rounded-md bg-brand-red px-4 py-3 text-center text-base font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-red-dark" data-id="element-624" data-mp="2jvfnrx0n4cqi">
                  CONTACT US
                </a>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}